import { useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { WeekPlanSub } from "@/components/tasks/WeekPlanMenu";
import {
  MoreVertical,
  Edit,
  Trash2,
  CheckCircle2,
  Calendar,
  User,
  Users,
  Building2,
  ArrowUpCircle,
  ArrowRightCircle,
  ArrowDownCircle,
  ArrowRight,
  ArrowLeft,
  Sun,
  Phone,
} from "lucide-react";

const priorityConfig = {
  "Hoch": { color: "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800", icon: ArrowUpCircle, accent: "border-l-red-500" },
  "Mittel": { color: "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-800", icon: ArrowRightCircle, accent: "border-l-yellow-500" },
  "Niedrig": { color: "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-800", icon: ArrowDownCircle, accent: "border-l-green-500" },
};

const statusConfig = {
  "Offen": "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
  "In Bearbeitung": "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800",
  "Erledigt": "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800",
};

const STATUS_ORDER = ["Offen", "In Bearbeitung", "Erledigt"];
const SWIPE_THRESHOLD = 70; // px to trigger a status change

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  });
};

const isOverdue = (dueDate) => {
  if (!dueDate) return false;
  return new Date(dueDate) < new Date() && new Date(dueDate).toDateString() !== new Date().toDateString();
};

export function TaskCard({ task, onEdit, onDelete, onStatusChange, isInMyDay = false, onToggleMyDay, onPlanWeek }) {
  const PriorityIcon = priorityConfig[task.priority]?.icon || ArrowRightCircle;
  const overdue = isOverdue(task.due_date) && task.status !== "Erledigt";

  // Swipe-to-change-status (mobile / touch only)
  const idx = STATUS_ORDER.indexOf(task.status);
  const nextStatus = idx >= 0 && idx < STATUS_ORDER.length - 1 ? STATUS_ORDER[idx + 1] : null;
  const prevStatus = idx > 0 ? STATUS_ORDER[idx - 1] : null;

  const [dx, setDx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const dragging = useRef(false);
  const decided = useRef(false);

  const handleTouchStart = (e) => {
    if (!onStatusChange) return;
    const t = e.touches[0];
    startX.current = t.clientX;
    startY.current = t.clientY;
    dragging.current = false;
    decided.current = false;
    setAnimating(false);
  };

  const handleTouchMove = (e) => {
    if (!onStatusChange) return;
    const t = e.touches[0];
    const deltaX = t.clientX - startX.current;
    const deltaY = t.clientY - startY.current;
    if (!decided.current) {
      if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
      decided.current = true;
      dragging.current = Math.abs(deltaX) > Math.abs(deltaY);
    }
    if (!dragging.current) return;
    let d = deltaX;
    // rubber-band resistance when no target status in that direction
    if ((d > 0 && !nextStatus) || (d < 0 && !prevStatus)) d = d / 4;
    setDx(d);
  };

  const handleTouchEnd = () => {
    if (dragging.current) {
      if (dx > SWIPE_THRESHOLD && nextStatus) {
        onStatusChange(task, nextStatus);
      } else if (dx < -SWIPE_THRESHOLD && prevStatus) {
        onStatusChange(task, prevStatus);
      }
    }
    setAnimating(true);
    setDx(0);
    dragging.current = false;
    decided.current = false;
  };

  const revealBg = dx > 0 ? "bg-green-100" : dx < 0 ? "bg-blue-100" : "";

  const handleCardKeyDown = (e) => {
    // Nur reagieren, wenn die Karte selbst fokussiert ist (nicht ein innerer Button/Menü)
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === "e" || e.key === "E") {
      e.preventDefault();
      onEdit(task);
    } else if (e.key === "Delete" && onDelete) {
      e.preventDefault();
      onDelete(task);
    }
  };

  return (
    <div
      className={`relative overflow-hidden rounded-lg lg:overflow-visible ${revealBg}`}
      style={{ touchAction: "pan-y" }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      data-testid={`task-swipe-${task.id}`}
    >
      {/* Swipe reveal indicators (mobile only) */}
      {dx !== 0 && (
        <div className="absolute inset-0 flex items-center justify-between px-5 lg:hidden pointer-events-none" data-testid={`task-swipe-hint-${task.id}`}>
          <span className={`flex items-center gap-1.5 text-sm font-semibold text-green-800 transition-opacity ${dx > 0 ? "opacity-100" : "opacity-0"}`}>
            <ArrowRight className="w-4 h-4" />
            {nextStatus || "—"}
          </span>
          <span className={`flex items-center gap-1.5 text-sm font-semibold text-blue-800 transition-opacity ${dx < 0 ? "opacity-100" : "opacity-0"}`}>
            {prevStatus || "—"}
            <ArrowLeft className="w-4 h-4" />
          </span>
        </div>
      )}

      <div
        style={{
          transform: `translateX(${dx}px)`,
          transition: animating ? "transform 0.2s ease-out" : "none",
        }}
      >
        <Card
          tabIndex={0}
          role="button"
          aria-label={`Aufgabe ${task.title} – Enter zum Bearbeiten, Entf zum Löschen`}
          onKeyDown={handleCardKeyDown}
          className={`hover:shadow-md transition-shadow border-l-4 focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${overdue ? "border-l-red-500 border-red-300" : priorityConfig[task.priority]?.accent || "border-l-transparent"}`}
          data-testid={`task-card-${task.id}`}
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <button
                  type="button"
                  onClick={() => onEdit(task)}
                  className="text-left w-full group"
                  data-testid={`task-card-edit-${task.id}`}
                  title="Bearbeiten"
                >
                  <h3 className="font-medium truncate group-hover:text-primary transition-colors">{task.title}</h3>
                  {task.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                      {task.description}
                    </p>
                  )}
                </button>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(task)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Bearbeiten
                  </DropdownMenuItem>
                  {task.status !== "Erledigt" && (
                    <DropdownMenuItem onClick={() => onStatusChange(task, "Erledigt")}>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Als erledigt markieren
                    </DropdownMenuItem>
                  )}
                  {onPlanWeek && task.status !== "Erledigt" && (
                    <WeekPlanSub task={task} onPlanWeek={onPlanWeek} />
                  )}
                  <DropdownMenuItem
                    onClick={() => onDelete(task)}
                    className="text-destructive"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Löschen
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              <Badge
                variant="outline"
                className={statusConfig[task.status] || ""}
                data-testid={`task-status-badge-${task.id}`}
              >
                {task.status}
              </Badge>
              {task.assigned_to_user_id === "general" && (
                <Badge
                  variant="outline"
                  className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800"
                  data-testid={`task-general-badge-${task.id}`}
                >
                  <Users className="w-3 h-3 mr-1" />
                  Allgemein
                </Badge>
              )}
              <Badge
                variant="outline"
                className={priorityConfig[task.priority]?.color}
              >
                <PriorityIcon className="w-3 h-3 mr-1" />
                {task.priority}
              </Badge>

              {task.due_date && (
                <Badge
                  variant="outline"
                  className={overdue ? "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800" : ""}
                >
                  <Calendar className="w-3 h-3 mr-1" />
                  {formatDate(task.due_date)}
                </Badge>
              )}
            </div>

            {((task.assigned_to_user_name && task.assigned_to_user_id !== "general") || task.customer_name) && (
              <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-muted-foreground">
                {task.assigned_to_user_name && task.assigned_to_user_id !== "general" && (
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {task.assigned_to_user_name}
                  </span>
                )}
                {task.customer_name && (
                  <span className="flex items-center gap-1">
                    <Building2 className="w-3 h-3" />
                    {task.customer_name}
                  </span>
                )}
                {task.customer_phone && (
                  <a
                    href={`tel:${task.customer_phone.replace(/[^0-9+]/g, "")}`}
                    onClick={(e) => e.stopPropagation()}
                    className="ml-auto shrink-0 inline-flex items-center gap-1 rounded-full bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-300 px-2 py-0.5 font-medium transition-colors"
                    title={`Kunde anrufen: ${task.customer_phone}`}
                    data-testid={`task-call-${task.id}`}
                  >
                    <Phone className="w-3 h-3" />
                    Anrufen
                  </a>
                )}
              </div>
            )}

            {onToggleMyDay && task.status !== "Erledigt" && (
              <Button
                variant={isInMyDay ? "secondary" : "outline"}
                size="sm"
                className={`mt-3 h-8 ${isInMyDay ? "text-primary" : "text-muted-foreground"}`}
                onClick={() => onToggleMyDay(task)}
                data-testid={`task-myday-toggle-${task.id}`}
                title={isInMyDay ? "Aus Mein Tag entfernen" : "Für heute einplanen"}
              >
                <Sun className={`w-4 h-4 mr-1.5 ${isInMyDay ? "fill-current" : ""}`} />
                {isInMyDay ? "In Mein Tag" : "Mein Tag"}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
